import React, { useState, useEffect, useRef, useMemo } from "react";
// lucide icons cleaned
import ActivityFormInputs from "./ActivityFormInputs";
import WorkspaceTabs from "./WorkspaceTabs";
import TextEditor from "./TextEditor";
import DrawingCanvas from "./DrawingCanvas";
import ImageUpload from "./ImageUpload";
import DocumentUpload from "./DocumentUpload";
import FormActions from "./FormActions";
import useFormState from "../../../hooks/useFormState";
import useDrafts from "../../../hooks/useDrafts";
import { saveActivity, fetchStudentActivities } from "../../../services/studentActivityService";
import { getValidationError } from "../../../utils/validationHelpers";
import { getLocalDateString } from "../../../utils/dateHelpers";
import ErrorMessage from "../common/ErrorMessage";
import Toast from "../common/Toast";
import { logAuditEvent } from "../../../services/auditService";
import { confirmDialog } from '../../../utils/notify';

const NewActivityForm = ({
  userId,
  editingActivity = null,
  loadedDraft = null,
  activities = [],
  onCancel,
  onSuccess,
}) => {
  const [workspaceMode, setWorkspaceMode] = useState("text");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  const currentDraftId = useRef(null);

  const userDepartment = useMemo(() => {
    try {
      const u = JSON.parse(
        localStorage.getItem("userData") || localStorage.getItem("user") || "{}"
      );
      return u.department || (u.departments && u.departments[0]) || "";
    } catch (e) {
      return "";
    }
  }, []);

  const {
    formData,
    isDirty,
    updateField,
    loadActivity,
    loadDraft,
    prepareForSubmit,
  } = useFormState(userId);

  const { saveDraft, clearAutoSave } = useDrafts();

  const [activityCountsByDate, setActivityCountsByDate] = useState({});

  useEffect(() => {
    fetchStudentActivities({ page_size: 9999 })
      .then((data) => {
        const items = data.activities || [];
        const counts = {};
        items.forEach((act) => {
          if (act.date) {
            const dateStr = getLocalDateString(act.date);
            if (dateStr) {
              counts[dateStr] = (counts[dateStr] || 0) + 1;
            }
          }
        });
        setActivityCountsByDate(counts);
      })
      .catch(() => {});
  }, []);


  useEffect(() => {
    if (editingActivity) {
      loadActivity(editingActivity);
    }
  }, [editingActivity, loadActivity]);

  useEffect(() => {
    if (loadedDraft) {
      loadDraft(loadedDraft);
      currentDraftId.current = loadedDraft.id || null;
    }
  }, [loadedDraft, loadDraft]);

  useEffect(() => {
    if (isDirty && !editingActivity) {
      if (
        formData.title ||
        formData.date ||
        formData.type ||
        formData.content ||
        formData.drawing ||
        (formData.images && formData.images.length > 0) ||
        (formData.documents && formData.documents.length > 0)
      ) {
        saveDraft({
          title: formData.title || "",
          date: formData.date || null,
          type: formData.type || "",
          content: formData.content || "",
          drawing: formData.drawing || null,
          images: formData.images || [],
          documents: formData.documents || [],
          competency_address: formData.competencyAddress || "",
          attempt_type: formData.attemptType || "",
          procedure_category: formData.procedureCategory || "",
          procedure_sub_type: formData.procedureSubtype || "",
          draft_id: currentDraftId.current,
        })
          .then((result) => {
            if (result && result.draft_id) {
              currentDraftId.current = result.draft_id;
            }
          })
          .catch(() => {});
      }
    }
  }, [formData, isDirty, editingActivity, saveDraft]);

  const imageQuota = useMemo(() => {
    if (!formData.date) return null;
    const targetDateStr = getLocalDateString(formData.date);
    const existingCount = activities
      .filter((a) => {
        if (!a.date) return false;
        return getLocalDateString(a.date) === targetDateStr && a.id !== formData.id;
      })
      .reduce((total, a) => total + (a.images?.length || 0), 0);

    const currentCount = formData.images?.length || 0;
    const totalUsed = existingCount + currentCount;

    return {
      used: totalUsed,
      limit: 10,
      remaining: Math.max(0, 10 - totalUsed),
      existingForDate: existingCount,
      currentActivity: currentCount,
    };
  }, [activities, formData.date, formData.id, formData.images]);

  const documentQuota = useMemo(() => {
    if (!formData.date) return null;
    const targetDateStr = getLocalDateString(formData.date);
    const existingCount = activities
      .filter((a) => {
        if (!a.date) return false;
        return getLocalDateString(a.date) === targetDateStr && a.id !== formData.id;
      })
      .reduce((total, a) => total + (a.documents?.length || 0), 0);

    const currentCount = formData.documents?.length || 0;
    const totalUsed = existingCount + currentCount;

    return {
      used: totalUsed,
      total: 1,
      remaining: Math.max(0, 1 - totalUsed),
      existingForDate: existingCount,
      currentActivity: currentCount,
    };
  }, [activities, formData.date, formData.id, formData.documents]);

  const validateForm = () => {
    const newErrors = {};

    const titleError = getValidationError("title", formData.title);
    if (titleError) newErrors.title = titleError;

    const dateError = getValidationError("date", formData.date);
    if (dateError) newErrors.date = dateError;

    const typeError = getValidationError("type", formData.type);
    if (typeError) newErrors.type = typeError;

    const isProcedureSelected =
      formData.type === "Procedure" || formData.type === "Procedures";

    if (isProcedureSelected) {
      if (!formData.procedureCategory || !formData.procedureCategory.trim()) {
        newErrors.procedureCategory = "Please select a Procedure Category";
      }
      if (!formData.procedureSubtype || !formData.procedureSubtype.trim()) {
        newErrors.procedureSubtype = "Please select a Procedure";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setToast({
        type: "error",
        message: "Please fill in all required fields marked in red.",
      });
      return;
    }

    try {
      setSaving(true);
      const activityData = prepareForSubmit();

      if (currentDraftId.current) {
        activityData.draft_id = currentDraftId.current;
      }

      const result = await saveActivity(activityData);

      try {
        const currentUser = JSON.parse(
          localStorage.getItem("userData") || localStorage.getItem("user") || "{}"
        );
        logAuditEvent(
          editingActivity ? "UPDATE" : "CREATE",
          "Student Logbook",
          `${editingActivity ? "Updated" : "Submitted"} clinical entry: ${activityData.procedure_category || activityData.procedureCategory || "Procedures"} - ${activityData.procedure_subtype || activityData.procedureSubtype || activityData.title || "Clinical Record"}`,
          currentUser
        );
      } catch (logErr) {}

      currentDraftId.current = null;
      setToast({
        type: "success",
        message: editingActivity
          ? "Activity updated successfully"
          : "Activity saved successfully",
      });

      setTimeout(() => {
        onSuccess(result);
      }, 400);
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to save activity",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (isDirty) {
      const confirmLeave = await confirmDialog(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmLeave) return;
    }
    onCancel();
  };

  const showAutoSaveIndicator = isDirty && !editingActivity;

  return (
    <div className="w-full space-y-6 pb-20 font-sans text-slate-900">


      {/* Main Workspace Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 space-y-8">
        {/* Form Inputs Grid */}
        <section className="space-y-4">
          <div className="border-b border-slate-100 pb-3">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-900" />
              Activity Details
            </h2>
          </div>

          <ActivityFormInputs
            formData={formData}
            updateField={updateField}
            errors={errors}
            userDepartment={userDepartment}
            activityCountsByDate={activityCountsByDate}
          />
        </section>

        {/* Content Workspace Section */}
        <section className="space-y-4 pt-2 border-t border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
                Entry Workspace & Attachments
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Add text logs, hand drawings, photos, or official clinical documents.
              </p>
            </div>
          </div>

          <WorkspaceTabs
            activeTab={workspaceMode}
            onTabChange={setWorkspaceMode}
          />

          <div className="min-h-[320px] bg-slate-50/40 rounded-xl border border-slate-200/80 p-4 sm:p-6">
            {workspaceMode === "text" && (
              <TextEditor
                value={formData.content}
                onChange={(value) => updateField("content", value)}
              />
            )}

            {workspaceMode === "draw" && (
              <DrawingCanvas
                drawing={formData.drawing}
                onDrawingChange={(drawing) => updateField("drawing", drawing)}
              />
            )}

            {workspaceMode === "image" && (
              <ImageUpload
                images={formData.images}
                onImagesChange={(images) => updateField("images", images)}
                imageQuota={imageQuota}
                selectedDate={formData.date}
              />
            )}

            {workspaceMode === "document" && (
              <DocumentUpload
                documents={formData.documents}
                onDocumentsChange={(documents) =>
                  updateField("documents", documents)
                }
                documentQuota={documentQuota}
                selectedDate={formData.date}
              />
            )}
          </div>
        </section>

        {/* Errors Banner */}
        {Object.keys(errors).length > 0 && (
          <ErrorMessage
            message="Please complete all required fields marked in red above before submitting."
            type="error"
          />
        )}
      </div>

      {/* Form Action Buttons Bar */}
      <FormActions
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        isEditing={!!editingActivity}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default NewActivityForm;
